import React, { useEffect, useMemo, useState } from "react";

import { Form, Modal } from "react-bootstrap";
import { CalendarIcon, FilterIcon, UserIcon } from "../Icons";
import { FaDollarSign, FaPlus } from "react-icons/fa";
import "../../assets/scss/create-target.scss";
import NumberCustomInput from "../utils/NumberCustomInput";
import Select from "react-select";
import Datetime from "react-datetime";
import { debounce, isEmpty, uniqBy } from "lodash";
import AsyncSelect from "react-select/async";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQuery } from "react-query";
import { customerFullName, customerTypeOptions } from "../../utils/helpers";
import queryString from "query-string";

import { toast } from "react-toastify";

import {
  isValid,
  addMonths,
  addYears,
  addDays,
  addWeeks,
  addQuarters,
  differenceInCalendarDays,
  format,
} from "date-fns";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import moment from "moment";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { useCurrencies } from "../../utils/hooks";

const incentiveTypeOptions = [
  { label: "Fixed Amount", value: "fixed" },
  { label: "Per Bag", value: "per-bag" },
  { label: "Per Truck", value: "truck-rate" },
  { label: "Percentage", value: "percentage" },
];

function getIncentiveValueLabel(type) {
  switch (type) {
    case "percentage":
      return "Percentage (%)";
    case "per-bag":
      return "Amount per Bag (₦)";
    case "truck-rate":
      return "Amount per Truck (₦)";
    case "fixed":
      return "Fixed Amount (₦)";
    default:
      return "Value";
  }
}

function IncentiveConfigSection({ salesTarget, setSalesTarget }) {
  const { prevailingCurrencySymbol } = useCurrencies();
  return (
    <>
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="enable-incentive-switch"
          label={<span>Enable Sales Rep Incentive</span>}
          checked={salesTarget.enableIncentive}
          onChange={(e) => {
            setSalesTarget({
              ...salesTarget,
              enableIncentive: e.target.checked,
            });
          }}
        />
        <Form.Text muted>
          Paid only when the customer meets their own quantity target and pays
          within the payment window above.
        </Form.Text>
      </Form.Group>

      {salesTarget.enableIncentive && (
        <div className="col-md-12">
          {/* --- TIER 1: Target Met --- */}
          <div className="row mb-3">
            <div className="col-12">
              <Form.Label className="fw-bold mb-1">
                Rate — Up to Target (Met)
              </Form.Label>
              <Form.Text muted className="d-block mb-2">
                Applies to qualifying units from 0 up to the target amount.
              </Form.Text>
            </div>

            <Form.Group className="col-md-6">
              <Form.Label>Incentive Structure</Form.Label>
              <Select
                classNamePrefix="form-select"
                options={incentiveTypeOptions}
                value={
                  incentiveTypeOptions.find(
                    (el) => el.value === salesTarget.incentiveType,
                  ) || null
                }
                onChange={({ value }) => {
                  setSalesTarget({ ...salesTarget, incentiveType: value });
                }}
              />
            </Form.Group>

            <Form.Group className="col-md-6">
              <Form.Label>
                {getIncentiveValueLabel(salesTarget.incentiveType)}
              </Form.Label>

              {salesTarget.incentiveType !== "percentage" ? (
                <CurrencyCustomInput
                  currencySymbol={prevailingCurrencySymbol}
                  className="border-0 px-1 w-100 "
                  value={salesTarget.incentiveValue}
                  onKeyDown={(e) => e.keyCode === 13 && e.target.blur()}
                  name="incentiveValue"
                  onValueChange={(value) => {
                    setSalesTarget({ ...salesTarget, incentiveValue: value });
                  }}
                  //  disableGroupSeparators
                  allowNegativeValue={false}
                  allowDecimals={true}
                  //   disabled={selectedItems}
                />
              ) : (
                <NumberCustomInput
                  placeholder="0"
                  name="incentiveValue"
                  value={salesTarget.incentiveValue}
                  onValueChange={(value) => {
                    setSalesTarget({ ...salesTarget, incentiveValue: value });
                  }}
                />
              )}
            </Form.Group>
          </div>

          {/* --- TIER 2: Target Exceeded --- */}
          <div className="row mb-3">
            <div className="col-12">
              <Form.Label className="fw-bold mb-1">
                Rate — Beyond Target (Exceeded)
              </Form.Label>
              <Form.Text muted className="d-block mb-2">
                Applies only to qualifying units above the target amount.
              </Form.Text>
            </div>

            <Form.Group className="col-md-6">
              <Form.Label>Incentive Structure</Form.Label>
              <Select
                classNamePrefix="form-select"
                options={incentiveTypeOptions}
                value={
                  incentiveTypeOptions.find(
                    (el) => el.value === salesTarget.exceededIncentiveType,
                  ) || null
                }
                onChange={({ value }) => {
                  setSalesTarget({
                    ...salesTarget,
                    exceededIncentiveType: value,
                  });
                }}
              />
            </Form.Group>

            <Form.Group className="col-md-6">
              <Form.Label>
                {getIncentiveValueLabel(salesTarget.exceededIncentiveType)}
              </Form.Label>

              {salesTarget?.exceededIncentiveType !== "percentage" ? (
                <CurrencyCustomInput
                  currencySymbol={prevailingCurrencySymbol}
                  className="border-0 px-1 w-100 "
                  value={salesTarget.exceededIncentiveValue}
                  onKeyDown={(e) => e.keyCode === 13 && e.target.blur()}
                  name="exceededIncentiveValue"
                  onValueChange={(value) => {
                    setSalesTarget({
                      ...salesTarget,
                      exceededIncentiveValue: value,
                    });
                  }}
                  //  disableGroupSeparators
                  allowNegativeValue={false}
                  allowDecimals={true}
                  //   disabled={selectedItems}
                />
              ) : (
                <NumberCustomInput
                  placeholder="0"
                  name="exceededIncentiveValue"
                  value={salesTarget.exceededIncentiveValue}
                  onValueChange={(value) => {
                    setSalesTarget({
                      ...salesTarget,
                      exceededIncentiveValue: value,
                    });
                  }}
                />
              )}
            </Form.Group>
          </div>

          {/* --- TIER 3: Target Not Met --- */}
          <div className="row mb-3">
            <div className="col-12">
              <Form.Check
                type="switch"
                id="enable-not-met-incentive-switch"
                label={<span>Pay something when target is NOT met</span>}
                checked={salesTarget.enableNotMetIncentive}
                onChange={(e) => {
                  setSalesTarget({
                    ...salesTarget,
                    enableNotMetIncentive: e.target.checked,
                    ...(e.target.checked
                      ? {}
                      : {
                          notMetIncentiveType: null,
                          notMetIncentiveValue: null,
                        }),
                  });
                }}
              />
              <Form.Text muted className="d-block mb-2">
                Off by default — sales rep earns nothing if the target isn't
                met. Enable this only if a reduced rate should apply instead.
              </Form.Text>
            </div>

            {salesTarget.enableNotMetIncentive && (
              <>
                <Form.Group className="col-md-6">
                  <Form.Label>Incentive Structure</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={incentiveTypeOptions}
                    value={
                      incentiveTypeOptions.find(
                        (el) => el.value === salesTarget.notMetIncentiveType,
                      ) || null
                    }
                    onChange={({ value }) => {
                      setSalesTarget({
                        ...salesTarget,
                        notMetIncentiveType: value,
                      });
                    }}
                  />
                </Form.Group>

                <Form.Group className="col-md-6">
                  <Form.Label>
                    {getIncentiveValueLabel(salesTarget.notMetIncentiveType)}
                  </Form.Label>

                  {salesTarget?.notMetIncentiveType !== "percentage" ? (
                    <CurrencyCustomInput
                      currencySymbol={prevailingCurrencySymbol}
                      className="border-0 px-1 w-100 "
                      value={salesTarget.notMetIncentiveValue}
                      onKeyDown={(e) => e.keyCode === 13 && e.target.blur()}
                      name="notMetIncentiveValue"
                      onValueChange={(value) => {
                        setSalesTarget({
                          ...salesTarget,
                          notMetIncentiveValue: value,
                        });
                      }}
                      //  disableGroupSeparators
                      allowNegativeValue={false}
                      allowDecimals={true}
                      //   disabled={selectedItems}
                    />
                  ) : (
                    <NumberCustomInput
                      placeholder="0"
                      name="notMetIncentiveValue"
                      value={salesTarget.notMetIncentiveValue}
                      onValueChange={(value) => {
                        setSalesTarget({
                          ...salesTarget,
                          notMetIncentiveValue: value,
                        });
                      }}
                    />
                  )}
                </Form.Group>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Converts a unit + value into an actual day count, anchored to a reference
// date so month/quarter/year conversions aren't just naive multiplication
// (e.g. "1 month" from Jan 31 lands differently than from Feb 1).
function convertUnitToDays(unit, value, referenceDate = new Date()) {
  const amount = Number(value) || 0;

  if (unit === "days") return amount;

  let futureDate;
  switch (unit) {
    case "weeks":
      futureDate = addWeeks(referenceDate, amount);
      break;
    case "months":
      futureDate = addMonths(referenceDate, amount);
      break;
    case "quarter":
      futureDate = addQuarters(referenceDate, amount);
      break;
    case "biannual":
      futureDate = addMonths(referenceDate, amount * 6);
      break;
    case "years":
      futureDate = addYears(referenceDate, amount);
      break;
    default:
      return amount;
  }

  return Math.max(0, differenceInCalendarDays(futureDate, referenceDate));
}

function getDateRange(period, referenceDate = new Date()) {
  if (!isValid(referenceDate)) {
    throw new Error("Invalid reference date provided");
  }

  const now = new Date(referenceDate);
  let startDate = now;
  let endDate = now;

  const normalizedPeriod = period.trim().toLowerCase();

  switch (normalizedPeriod) {
    case "monthly":
      endDate = addMonths(now, 1);
      break;

    case "quarterly":
      endDate = addMonths(now, 3);
      break;

    case "bi-annual":
    case "biannual":
    case "semi-annual":
    case "semiannual":
      endDate = addMonths(now, 6);
      break;

    case "yearly":
    case "annual":
      endDate = addYears(now, 1);
      break;

    default:
      throw new Error(
        `Invalid period: ${period}. Supported periods: daily, weekly, monthly, quarterly, bi-annual, yearly`,
      );
  }

  return {
    startDate,
    endDate,
    period: normalizedPeriod,
  };
}

const SalesTargetModal = ({
  show,
  setShow,
  refetch,
  isUpdate = false,
  target,
}) => {
  const { backendUrl, token } = useAuth();

  const [activeTab, setActiveTab] = useState("customer");

  const [customerTarget, setCustomerTarget] = useState({
    targetValue: 1,
    period: "monthly",
    startDate: getDateRange("monthly").startDate,
    endDate: getDateRange("monthly").endDate,
  });
  const { prevailingCurrencySymbol } = useCurrencies();

  const [salesTarget, setSalesTarget] = useState({
    targetTitle: "",
    targetValue: 1,
    period: "monthly",
    targetType: "general",
    metricType: "revenue",
    customerType: "",
    customerGroup: "",
    specificCustomer: [],
    salesRep: [],
    items: [],
    product: [],
    category: [],
    paymentWindowDays: 7,
    enableIncentive: false,
    incentiveType: "fixed",
    incentiveValue: 0,
    startDate: getDateRange("monthly").startDate,
    endDate: getDateRange("monthly").endDate,

    exceededIncentiveType: "fixed",
    exceededIncentiveValue: 0,

    enableNotMetIncentive: false,
    notMetIncentiveType: "fixed",
    notMetIncentiveValue: 0,
    linkIncentiveToPayroll: false,
  });

  const [paymentWindow, setPaymentWindow] = useState({
    mode: "duration", // "duration" | "dateRange"
    unit: "days", // days | weeks | months | quarter | biannual | years
    value: 7,
    dateRange: [], // [Date, Date] when mode === "dateRange"
  });

  const paymentWindowDays = useMemo(() => {
    if (paymentWindow?.mode === "dateRange") {
      const [start, end] = paymentWindow.dateRange || [];
      if (start && end) {
        return Math.max(0, differenceInCalendarDays(end, start));
      }
      return 0;
    }
    return convertUnitToDays(paymentWindow.unit, paymentWindow.value);
  }, [paymentWindow]);

  useEffect(() => {
    if (isUpdate && !isEmpty(target)) {
      if (target.companyTarget === "customer") {
        setCustomerTarget({
          ...customerTarget,
          period: target.period,
          targetValue: Number(target.targetAmount),
          endDate: new Date(target.endDate),
          startDate: new Date(target.startDate),
        });
      } else {
        setSalesTarget({
          ...salesTarget,
          period: target.period,
          targetValue: Number(target.targetAmount),
          endDate: new Date(target.endDate),
          startDate: new Date(target.startDate),
          customerGroup: target.targetGroup,
          customerType: target.targetGroup,
          specificCustomer:
            target.targetType === "specific-customer"
              ? target?.targetGroup?.map((customer) => ({
                  ...customer,
                  value: customer.Cust_ID,
                  label: customer?.label,
                }))
              : [],

          salesRep:
            target.targetType === "sales-rep"
              ? target?.targetGroup?.map((rep) => ({
                  ...rep,
                  value: rep.value,
                  label: rep?.label,
                }))
              : [],
          items: target?.itemFilter?.items || [],
          product: target?.itemFilter?.product || [],
          category: target?.itemFilter?.category || [],
          targetType: target.targetType,
          metricType: target.metricType || "revenue",
          paymentWindowDays: target.paymentWindowDays ?? 7,
          enableIncentive: !!Number(target.enableIncentive),
          incentiveType: target.incentiveType || "fixed",
          incentiveValue: Number(target.incentiveValue) || 0,
          exceededIncentiveType: target?.exceededIncentiveType,
          exceededIncentiveValue: target?.exceededIncentiveValue,

          enableNotMetIncentive: target?.enableNotMetIncentive ?? false,
          notMetIncentiveType: target?.notMetIncentiveType,
          notMetIncentiveValue: target?.notMetIncentiveValue,
          targetTitle: target?.targetTitle ?? "",
          linkIncentiveToPayroll:
            Boolean(target?.linkIncentiveToPayroll) ?? false,
        });

        let dateRange = [];
        let windowPeriodConfig = {
          mode: "duration", // "duration" | "dateRange"
          unit: "days", // days | weeks | months | quarter | biannual | years
          value: target?.paymentWindowDays,
          dateRange: [],
        };
        if (
          target?.paymentWindowConfig &&
          !isEmpty(target?.paymentWindowConfig?.dateRange)
        ) {
          dateRange = [
            new Date(target?.paymentWindowConfig?.dateRange[0]),
            new Date(target?.paymentWindowConfig?.dateRange[1]),
          ];

          windowPeriodConfig = { ...target?.paymentWindowConfig, dateRange };
        }

        setPaymentWindow((prev) => windowPeriodConfig);
      }
      setActiveTab(
        target.companyTarget === "customer" ? "customer" : "revenue",
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTarget = async (payload) => {
    let endpoint = isUpdate
      ? `${backendUrl}/api/company/update-target/${target.id}`
      : `${backendUrl}/api/company/create-target`;
    let response = await fetch(endpoint, {
      method: isUpdate ? "PATCH" : "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const createTargetMutation = useMutation((payload) => createTarget(payload), {
    onSuccess: ({ message }) => {
      refetch();
      toast.success(message);
      setShow(false);
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  const periods = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "bi-annual", label: "Bi-Annual" },
    { value: "yearly", label: "Yearly" },
  ];

  const targetScopeOptions = [
    { value: "general", label: "General" },
    { value: "customer-type", label: "By Customer Type" },
    { value: "customer-group", label: "By Customer Group" },
    { value: "specific-customer", label: "Specific Customer" },
    { value: "sales-rep", label: "By Sales Rep" },
  ];

  const metricTypeOptions = [
    { value: "revenue", label: "Revenue" },
    { value: "bags", label: "Bags" },
    { value: "trucks", label: "Trucks" },
  ];

  const incentiveTypeOptions = [
    { value: "fixed", label: "Fixed Amount" },
    { value: "per-bag", label: "Per Bag" },
    { value: "truck-rate", label: "Truck Rate" },
    { value: "percentage", label: "Percentage of Sales" },
  ];

  const amountLabel = {
    revenue: "Target Revenue Amount",
    bags: "Target Bag Quantity",
    trucks: "Target Truck Count",
  }[salesTarget.metricType];

  const incentiveValueLabel = {
    fixed: "Incentive Amount",
    "per-bag": "Rate Per Bag",
    "truck-rate": "Rate Per Truck",
    percentage: "Percentage of Sales (%)",
  }[salesTarget.incentiveType];

  const handleCustomerTargetSubmit = () => {
    const payload = {
      companyTarget: "customer",
      startDate: customerTarget.startDate,
      endDate: customerTarget.endDate,
      targetAmount: customerTarget.targetValue,
      period: customerTarget.period,
      targetType: "in-company",
      targetGroup: "in-company",
      items: [],
      products: [],
      categories: [],
    };
    createTargetMutation.mutate(payload);
  };

  const handleSalesTargetSubmit = async () => {
    const payload = {
      companyTarget: "revenue",
      startDate: salesTarget.startDate,
      endDate: salesTarget.endDate,
      targetAmount: salesTarget.targetValue,
      period: salesTarget.period,
      targetType: salesTarget.targetType,
      metricType: salesTarget.metricType,
      paymentWindowDays: paymentWindowDays,
      enableIncentive: salesTarget.enableIncentive,
      incentiveType: salesTarget.enableIncentive
        ? salesTarget.incentiveType
        : "fixed",
      incentiveValue: salesTarget.enableIncentive
        ? salesTarget.incentiveValue
        : 0,
      targetGroup:
        salesTarget.targetType === "general"
          ? "All customer"
          : salesTarget.targetType === "customer-type"
          ? salesTarget.customerType
          : salesTarget.targetType === "customer-group"
          ? salesTarget.customerGroup
          : salesTarget.targetType === "sales-rep"
          ? salesTarget.salesRep
          : salesTarget.specificCustomer,

      items: salesTarget.items,
      products: salesTarget.product,
      categories: salesTarget.category,
      paymentWindowConfig: paymentWindow,

      exceededIncentiveType: salesTarget?.exceededIncentiveType ?? "fixed",
      exceededIncentiveValue: salesTarget.exceededIncentiveValue ?? 0,

      enableNotMetIncentive: salesTarget.enableNotMetIncentive ?? false,
      notMetIncentiveType: salesTarget.notMetIncentiveType ?? "fixed",
      notMetIncentiveValue: salesTarget.notMetIncentiveValue ?? 0,
      targetTitle: salesTarget?.targetTitle,
      linkIncentiveToPayroll: salesTarget?.linkIncentiveToPayroll ?? false,
    };

    createTargetMutation.mutate(payload);
  };

  const fetchCustomers = async (inputValue = "", callback) => {
    try {
      let response = await fetch(
        `${backendUrl}/api/customers?page=1&Limit=50&LastName=${inputValue}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }
      const { data } = await response.json();
      if (data.customers) {
        data.customers = data.customers.map((el) => ({
          label: customerFullName(el),
          value: el.Cust_ID,
          Cust_ID: el.Cust_ID,
          email: el.Email,
          type: el.TransType,
          customerGroup: el.Group,
        }));

        if (callback) {
          callback(data?.customers || []);
        }
      }

      data.customerTypes = uniqBy(
        [
          ...customerTypeOptions,
          ...data.customerTypes
            .filter((el) => el.TransType)
            .map((el) => ({
              value: el.TransType,
              label: el.TransType,
            })),
        ],
        "value",
      );

      data.groups = uniqBy(
        [
          ...data.groups
            .filter((el) => el.Group)
            .map((el) => ({
              value: el.Group,
              label: el.Group,
            })),
        ],
        "value",
      );

      return data;
    } catch (err) {
      console.log(err);
      callback([]);
    }
  };

  // NOTE: endpoint/shape assumed to mirror fetchCustomers — confirm actual
  // sales-rep route and field names (RepID/Name) with backend before wiring up.
  const fetchSalesReps = async (inputValue = "", callback) => {
    try {
      let response = await fetch(
        `${backendUrl}/api/employees?usage=sales-rep&page=1&Limit=50&Name=${inputValue}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }
      const data = await response.json();
      const reps = (data.employees || []).map((el) => ({
        label: `${el.FirstName || ""} ${el.LastName || ""}`,
        value: el.EmployeeID,
        RepID: el.EmployeeID,
      }));

      if (callback) {
        callback(reps);
      }
      return { salesReps: reps };
    } catch (err) {
      console.log(err);
      if (callback) callback([]);
    }
  };

  // Pulls the `product` and `category` meta lists that the items endpoint
  // already returns on every call (see Cat_Name / Product_Name arrays).
  const fetchItemsMeta = async () => {
    try {
      let response = await fetch(
        `${backendUrl}/api/items?${queryString.stringify({
          page: 1,
          limit: 1,
        })}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }
      const { data } = await response.json();

      const products = uniqBy(
        (data?.product || [])
          .filter((el) => !isEmpty(el?.Product_Name))
          .map((el) => ({
            value: el.Product_Name,
            label: el.Product_Name,
          })),
        "value",
      );

      const categories = uniqBy(
        (data?.category || [])
          .filter((el) => !isEmpty(el?.Cat_Name))
          .map((el) => ({
            value: el.Cat_Name,
            label: el.Cat_Name,
          })),
        "value",
      );

      return { products, categories };
    } catch (err) {
      console.log(err);
      return { products: [], categories: [] };
    }
  };

  // Async search for individual items (Bar_Code / Item_Name), used by the
  // "Items" multi-select below.
  const fetchItems = async (inputValue = "", callback) => {
    try {
      let response = await fetch(
        `${backendUrl}/api/items?${queryString.stringify({
          page: 1,
          limit: 50,
          q: inputValue,
        })}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }
      const { data } = await response.json();
      const options = (data?.items || []).map((el) => ({
        label: el.Item_Name,
        value: el.Bar_Code,
        Bar_Code: el.Bar_Code,
      }));

      if (callback) callback(options);
      return options;
    } catch (err) {
      console.log(err);
      if (callback) callback([]);
    }
  };

  const { data } = useQuery(
    ["CUSTOMERS_LIST"],
    () => fetchCustomers(),
    { onSuccess: (data) => {} },
    {
      enabled: true,
      keepPreviousData: true,
    },
  );

  const { data: itemsMeta } = useQuery(["ITEMS_META"], fetchItemsMeta, {
    enabled: true,
    keepPreviousData: true,
  });

  const debouncedFetchCustomers = debounce(fetchCustomers, 500);
  const debouncedFetchSalesReps = debounce(fetchSalesReps, 500);
  const debouncedFetchItems = debounce(fetchItems, 500);

  const loadCustomerOptions = (inputValue, callback) => {
    try {
      if (isEmpty(inputValue)) {
        return callback(null, { options: [] });
      }
      debouncedFetchCustomers(inputValue, callback);
    } catch (err) {
      console.log(err);
      return callback(null, { options: [] });
    }
  };

  const loadSalesRepOptions = (inputValue, callback) => {
    try {
      debouncedFetchSalesReps(inputValue, callback);
    } catch (err) {
      console.log(err);
      return callback(null, { options: [] });
    }
  };

  const loadItemOptions = (inputValue, callback) => {
    try {
      console.log("this  is the item ", inputValue);

      debouncedFetchItems(inputValue, callback);
    } catch (err) {
      console.log(err);
      return callback(null, { options: [] });
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      animation={false}
      enforceFocus={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title as={"div"}>
          <h6>
            {isUpdate ? "Edit" : "Create"}
            Target
          </h6>
          <p>
            {isUpdate ? "Edit" : "Create"} and manage your customer acquisition,
            sales targets, and rep incentives
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={""}>
          <div className={"tab-container"}>
            <button
              className={`tab ${
                activeTab === "customer" ? "active-nav" : "inactive-nav"
              }`}
              onClick={() => setActiveTab("customer")}
            >
              <UserIcon size={20} />
              Customer Acquisition
            </button>
            <button
              className={`tab ${
                activeTab === "revenue" ? "active-nav" : "inactive-nav"
              }`}
              onClick={() => setActiveTab("revenue")}
            >
              <FaDollarSign size={20} />
              Sales Target
            </button>
          </div>

          {activeTab === "customer" && (
            <div className={""}>
              <h6 className={"form-title"}>
                <UserIcon size={24} />
                Customer Acquisition Target
              </h6>
              <div>
                <div className={"form-grid"}>
                  <Form.Group className={"form-group"}>
                    <Form.Label>Target Number of Customers</Form.Label>

                    <NumberCustomInput
                      placeholder="0"
                      name="targetValue"
                      value={customerTarget.targetValue}
                      onValueChange={(value) => {
                        setCustomerTarget({
                          ...customerTarget,
                          targetValue: value,
                        });
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>
                      <CalendarIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Period
                    </Form.Label>

                    <Select
                      classNamePrefix={`form-select`}
                      options={periods}
                      value={periods.find(
                        (el) => el.value === customerTarget.period,
                      )}
                      onChange={({ value }) => {
                        const date = getDateRange(value);

                        setCustomerTarget({
                          ...customerTarget,
                          period: value,
                          startDate: date.startDate,
                          endDate: date.endDate,
                        });
                      }}
                    />
                  </Form.Group>

                  <div className="row">
                    <Form.Group className=" col-md-6">
                      <Form.Label>Start Date</Form.Label>
                      <Datetime
                        dateFormat="yyyy-MM-dd HH:mm:ss"
                        timeFormat={true}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="startDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={customerTarget.startDate}
                        onChange={(date) => {
                          setCustomerTarget({
                            ...customerTarget,
                            startDate: format(new Date(date), "yyyy-MM-dd"),
                          });
                        }}
                      />
                    </Form.Group>
                    <Form.Group className=" col-md-6">
                      <Form.Label>End Date</Form.Label>

                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={true}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="endDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={customerTarget.endDate}
                        onChange={(date) => {
                          setCustomerTarget({
                            ...customerTarget,
                            endDate: format(new Date(date), "yyyy-MM-dd"),
                          });
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>

                <button
                  onClick={handleCustomerTargetSubmit}
                  className={"submit-button"}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#083d7a";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(10, 74, 149, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#0a4a95";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <FaPlus size={20} />
                  {createTargetMutation.isLoading
                    ? " Creating"
                    : " Create Customer Target"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "revenue" && (
            <div className={""}>
              <h6 className={"form-title"}>
                <FaDollarSign size={24} />
                Sales Target
              </h6>
              <div>
                <div
                  className={"row"}
                  // style={{
                  //   display: "grid",
                  //   gridTemplateColumns: "1fr 1fr",
                  //   columnGap: "1rem",
                  // }}
                >
                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>Target Title</Form.Label>
                    <Form.Control
                      value={salesTarget.targetTitle}
                      onChange={(e) => {
                        setSalesTarget({
                          ...salesTarget,
                          targetTitle: e.target.value,
                        });
                      }}
                    />
                  </Form.Group>
                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>
                      <FilterIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Scope
                    </Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={targetScopeOptions}
                      isSearchable
                      value={targetScopeOptions.find(
                        (el) => el.value === salesTarget.targetType,
                      )}
                      onChange={({ value }) => {
                        setSalesTarget({
                          ...salesTarget,
                          targetType: value,
                        });
                      }}
                    />
                  </Form.Group>

                  {salesTarget.targetType === "customer-type" && (
                    <div className={"col-md-6 mb-3"}>
                      <Form.Group>
                        <Form.Label>Customer Type</Form.Label>
                        <Select
                          classNamePrefix={`form-select`}
                          options={data?.customerTypes || []}
                          isSearchable
                          value={salesTarget?.customerType}
                          isMulti
                          onChange={(value) => {
                            setSalesTarget({
                              ...salesTarget,
                              customerType: value,
                            });
                          }}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {salesTarget.targetType === "customer-group" && (
                    <div className={"col-md-6 mb-3"}>
                      <Form.Group>
                        <Form.Label>Customer Group</Form.Label>
                        <Select
                          classNamePrefix={`form-select`}
                          isSearchable
                          options={data?.groups || []}
                          value={salesTarget?.customerGroup ?? []}
                          isMulti
                          onChange={(value) => {
                            setSalesTarget({
                              ...salesTarget,
                              customerGroup: value,
                            });
                          }}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {salesTarget.targetType === "specific-customer" && (
                    <div className={"col-md-6 mb-3"}>
                      <Form.Group>
                        <Form.Label>Specific Customer</Form.Label>
                        <AsyncSelect
                          classNamePrefix={"form-select"}
                          className="customer-select-dropdown w-100"
                          data={data?.customers || []}
                          menuPlacement="bottom"
                          menuPosition="fixed"
                          isSearchable={true}
                          isMulti
                          value={salesTarget?.specificCustomer ?? []}
                          defaultOptions={data?.customers || []}
                          onChange={(selection) => {
                            setSalesTarget({
                              ...salesTarget,
                              specificCustomer: selection,
                            });
                          }}
                          id={`target-specific-customer`}
                          loadOptions={loadCustomerOptions}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {salesTarget.targetType === "sales-rep" && (
                    <div className={"col-md-6 mb-3"}>
                      <Form.Group>
                        <Form.Label>Sales Rep</Form.Label>
                        <AsyncSelect
                          key={"sales-rep"}
                          classNamePrefix={"form-select"}
                          className="sales-rep-select-dropdown w-100"
                          menuPlacement="bottom"
                          menuPosition="fixed"
                          isSearchable={true}
                          isMulti
                          value={salesTarget?.salesRep ?? []}
                          onChange={(selection) => {
                            setSalesTarget({
                              ...salesTarget,
                              salesRep: selection,
                            });
                          }}
                          id={`target-sales-rep`}
                          loadOptions={loadSalesRepOptions}
                          defaultOptions
                          cacheOptions
                        />
                      </Form.Group>
                    </div>
                  )}

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>Items</Form.Label>
                    <AsyncSelect
                      key={"target-items"}
                      classNamePrefix={"form-select"}
                      className="items-select-dropdown w-100"
                      menuPlacement="bottom"
                      menuPosition="fixed"
                      isSearchable={true}
                      isMulti
                      value={salesTarget?.items ?? []}
                      onChange={(selection) => {
                        setSalesTarget({
                          ...salesTarget,
                          items: selection || [],
                        });
                      }}
                      id={`target-items`}
                      loadOptions={loadItemOptions}
                      defaultOptions
                      cacheOptions
                      placeholder="Search items..."
                    />
                  </Form.Group>

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>Product</Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={itemsMeta?.products || []}
                      isSearchable
                      isMulti
                      value={salesTarget?.product ?? []}
                      onChange={(selection) => {
                        setSalesTarget({
                          ...salesTarget,
                          product: selection || [],
                        });
                      }}
                      placeholder="Select product(s)..."
                    />
                  </Form.Group>

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>Category</Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={itemsMeta?.categories || []}
                      isSearchable
                      isMulti
                      value={salesTarget?.category ?? []}
                      onChange={(selection) => {
                        setSalesTarget({
                          ...salesTarget,
                          category: selection || [],
                        });
                      }}
                      placeholder="Select category(ies)..."
                    />
                  </Form.Group>

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>Target Metric</Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={metricTypeOptions}
                      value={metricTypeOptions.find(
                        (el) => el.value === salesTarget.metricType,
                      )}
                      onChange={({ value }) => {
                        setSalesTarget({
                          ...salesTarget,
                          metricType: value,
                        });
                      }}
                    />
                  </Form.Group>

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>{amountLabel}</Form.Label>

                    {salesTarget.metricType === "revenue" ? (
                      <CurrencyCustomInput
                        currencySymbol={prevailingCurrencySymbol}
                        className="border-0 px-1 w-100 "
                        value={salesTarget.targetValue}
                        onKeyDown={(e) => e.keyCode === 13 && e.target.blur()}
                        name="targetValue"
                        onValueChange={(value) => {
                          setSalesTarget({
                            ...salesTarget,
                            targetValue: value,
                          });
                        }}
                        //  disableGroupSeparators
                        allowNegativeValue={false}
                        allowDecimals={true}
                        //   disabled={selectedItems}
                      />
                    ) : (
                      <NumberCustomInput
                        placeholder="0"
                        name="targetValue"
                        value={salesTarget.targetValue}
                        onValueChange={(value) => {
                          setSalesTarget({
                            ...salesTarget,
                            targetValue: value,
                          });
                        }}
                      />
                    )}
                  </Form.Group>

                  <Form.Group className=" col-md-6 mb-3">
                    <Form.Label>
                      <CalendarIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Period
                    </Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={periods}
                      value={periods.find(
                        (el) => el.value === salesTarget.period,
                      )}
                      onChange={({ value }) => {
                        const date = getDateRange(value);
                        setSalesTarget({
                          ...salesTarget,
                          period: value,
                          startDate: date.startDate,
                          endDate: date.endDate,
                        });
                      }}
                    />
                  </Form.Group>

                  {/* <div className={"incentive-section col-md-6 mb-3"}>
                    <Form.Group className=" mb-3">
                      <Form.Check
                        type="switch"
                        id="enable-incentive-switch"
                        label={<span>Enable Sales Rep Incentive</span>}
                        checked={salesTarget.enableIncentive}
                        onChange={(e) => {
                          setSalesTarget({
                            ...salesTarget,
                            enableIncentive: e.target.checked,
                          });
                        }}
                      />
                      <Form.Text muted>
                        Paid only when the customer meets their own quantity
                        target and pays within the payment window above.
                      </Form.Text>
                    </Form.Group>

                    {salesTarget.enableIncentive && (
                      <div className={"col-md-12 "}>
                        <div className=" row">
                          <Form.Group className=" col-md-6">
                            <Form.Label>Incentive Structure</Form.Label>
                            <Select
                              classNamePrefix={`form-select`}
                              options={incentiveTypeOptions}
                              value={incentiveTypeOptions.find(
                                (el) => el.value === salesTarget.incentiveType,
                              )}
                              onChange={({ value }) => {
                                setSalesTarget({
                                  ...salesTarget,
                                  incentiveType: value,
                                });
                              }}
                            />
                          </Form.Group>

                          <Form.Group className="col-md-6">
                            <Form.Label>{incentiveValueLabel}</Form.Label>
                            <NumberCustomInput
                              placeholder="0"
                              name="incentiveValue"
                              value={salesTarget.incentiveValue}
                              onValueChange={(value) => {
                                setSalesTarget({
                                  ...salesTarget,
                                  incentiveValue: value,
                                });
                              }}
                            />
                          </Form.Group>
                        </div>
                      </div>
                    )}
                  </div> */}

                  <IncentiveConfigSection
                    salesTarget={salesTarget}
                    setSalesTarget={setSalesTarget}
                  />

                  {salesTarget?.enableIncentive && (
                    <div>
                      <Form.Group className="row   mb-3">
                        <Form.Label>Payment Window</Form.Label>

                        <div className="mb-2 col-md-4">
                          <Select
                            classNamePrefix="form-select"
                            options={[
                              { label: "Duration", value: "duration" },
                              { label: "Date Range", value: "dateRange" },
                            ]}
                            value={
                              [
                                { label: "Duration", value: "duration" },
                                { label: "Date Range", value: "dateRange" },
                              ].find((el) => el.value === paymentWindow.mode) ||
                              null
                            }
                            onChange={({ value }) => {
                              setPaymentWindow({
                                ...paymentWindow,
                                mode: value,
                              });
                            }}
                          />
                        </div>

                        {paymentWindow.mode === "duration" ? (
                          <div className="  col-md-8">
                            <div className="row">
                              <div className=" col-md-6 ">
                                <NumberCustomInput
                                  placeholder="0"
                                  name="paymentWindowValue"
                                  value={paymentWindow.value}
                                  onValueChange={(value) => {
                                    setPaymentWindow({
                                      ...paymentWindow,
                                      value,
                                    });
                                  }}
                                />
                              </div>

                              <div className="col-md-6 ">
                                <Select
                                  classNamePrefix="form-select"
                                  options={[
                                    { label: "Days", value: "days" },
                                    { label: "Weeks", value: "weeks" },
                                    { label: "Months", value: "months" },
                                    { label: "Quarter(s)", value: "quarter" },
                                    { label: "Bi-Annual", value: "biannual" },
                                    { label: "Years", value: "years" },
                                  ]}
                                  value={
                                    [
                                      { label: "Days", value: "days" },
                                      { label: "Weeks", value: "weeks" },
                                      { label: "Months", value: "months" },
                                      { label: "Quarter(s)", value: "quarter" },
                                      { label: "Bi-Annual", value: "biannual" },
                                      { label: "Years", value: "years" },
                                    ].find(
                                      (el) => el.value === paymentWindow.unit,
                                    ) || null
                                  }
                                  onChange={({ value }) => {
                                    setPaymentWindow({
                                      ...paymentWindow,
                                      unit: value,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className=" col-md-4">
                            <RsDateRangePicker
                              placement="topStart"
                              value={paymentWindow.dateRange}
                              onOk={(range) => {
                                setPaymentWindow({
                                  ...paymentWindow,
                                  dateRange: range,
                                });
                              }}
                              onClean={() => {
                                setPaymentWindow({
                                  ...paymentWindow,
                                  dateRange: [],
                                });
                              }}
                            />
                          </div>
                        )}

                        <Form.Text muted>
                          {paymentWindow.mode === "duration"
                            ? `≈ ${paymentWindowDays} day(s)`
                            : paymentWindow.dateRange?.length === 2
                            ? `${paymentWindowDays} day(s) between selected dates`
                            : "Select a start and end date"}
                          {" — "}number of days the customer has to pay for a
                          sale to count toward the rep incentive.
                        </Form.Text>
                      </Form.Group>
                      <div className="col-12">
                        <Form.Check
                          type="switch"
                          id="enable-not-met-incentive-switch"
                          label={<span>Link incentive payment to payroll</span>}
                          checked={salesTarget.linkIncentiveToPayroll}
                          onChange={(e) => {
                            setSalesTarget({
                              ...salesTarget,
                              linkIncentiveToPayroll: e.target.checked,
                            });
                          }}
                        />
                        <Form.Text muted className="d-block mb-2">
                          When enabled, earned incentives are included in the
                          sales rep's next payroll run instead of being paid out
                          separately.
                        </Form.Text>
                      </div>
                    </div>
                  )}

                  <div className={"row"}>
                    <Form.Group className=" col-md-6 mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="startDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={salesTarget.startDate}
                        onChange={(date) => {
                          setSalesTarget({
                            ...salesTarget,
                            startDate: new Date(date).toISOString(),
                          });
                        }}
                      />
                    </Form.Group>
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>End Date</Form.Label>
                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="endDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={salesTarget.endDate}
                        onChange={(date) => {
                          setSalesTarget({
                            ...salesTarget,
                            endDate: new Date(date).toISOString(),
                          });
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>

                <button
                  onClick={handleSalesTargetSubmit}
                  className={"submit-button"}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#083d7a";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(10, 74, 149, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#0a4a95";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <FaPlus size={20} />
                  {createTargetMutation.isLoading
                    ? " Creating"
                    : " Create Sales Target"}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SalesTargetModal;
